import { TokenStorage } from "../auth/TokenStorage";

type EventHandler<T = unknown> = (data: T) => void;

type SseParamValue = string | number | string[];

export interface SseClientOptions {
  url: string;
  // Отсутствует, когда подписка адресуется одним только url: строка запроса
  // тогда несёт лишь auth-token.
  params?: Record<string, SseParamValue>;
  events: Record<string, EventHandler>;
  // Отсутствует, когда вызывающему нечего показать на обрыве: клиент всё равно
  // переподключается сам, и разрыв остаётся невидимым для экрана.
  onError?: (error: Event) => void;
}

const INITIAL_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;
const BACKOFF_MULTIPLIER = 2;

export class SseClient {
  private readonly options: SseClientOptions;
  private eventSource: EventSource | null = null;
  private retryMs = INITIAL_RETRY_MS;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  // Точка, с которой подписка поднимается заново после паузы отката. Таймер
  // получает её как обычный колбэк: подключение и планирование повтора зовут
  // друг друга по кругу, и держать этот круг на прямых вызовах методов значило
  // бы читать класс в порядке, которого у взаимной рекурсии нет.
  private readonly reconnect = (): void => {
    this.retryTimeout = null;
    this.connect();
  };

  public constructor(options: SseClientOptions) {
    this.options = options;
    this.connect();
  }

  public close(): void {
    this.closed = true;
    this.clearRetryTimeout();
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private connect(): void {
    if (this.closed) {
      return;
    }

    const url = this.buildUrl();
    const eventSource = new EventSource(url);

    for (const [eventName, handler] of Object.entries(this.options.events)) {
      eventSource.addEventListener(eventName, (event: MessageEvent) => {
        this.retryMs = INITIAL_RETRY_MS;
        const data = JSON.parse(event.data as string) as unknown;
        handler(data);
      });
    }

    eventSource.onerror = (event: Event) => {
      if (this.closed) {
        return;
      }
      if (this.options.onError !== undefined) {
        this.options.onError(event);
      }
      eventSource.close();
      this.eventSource = null;
      this.scheduleReconnect();
    };

    this.eventSource = eventSource;
  }

  private scheduleReconnect(): void {
    if (this.closed) {
      return;
    }
    this.retryTimeout = setTimeout(this.reconnect, this.retryMs);
    this.retryMs = Math.min(this.retryMs * BACKOFF_MULTIPLIER, MAX_RETRY_MS);
  }

  private clearRetryTimeout(): void {
    if (this.retryTimeout !== null) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  private buildUrl(): string {
    const searchParameters = new URLSearchParams();
    const parameters = this.options.params;
    if (parameters) {
      for (const [key, value] of Object.entries(parameters)) {
        if (Array.isArray(value)) {
          for (const item of value) {
            searchParameters.append(key, item);
          }
        } else {
          searchParameters.set(key, String(value));
        }
      }
    }
    // EventSource cannot set custom headers; the access token is passed as the
    // auth-token query parameter, which the backend AuthPlugin also accepts.
    const token = TokenStorage.getAccessToken();
    if (token !== null) {
      searchParameters.set("auth-token", token);
    }
    const query = searchParameters.toString();
    return query === "" ? this.options.url : `${this.options.url}?${query}`;
  }
}
